import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, UtensilsCrossed, Flame, Bike, Phone } from "lucide-react";
import { BUSINESS } from "../data/business";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { icon: Star, value: "4.5+", label: "Google ocena", cls: "text-ember" },
  { icon: UtensilsCrossed, value: "20", label: "priloga po izboru", cls: "text-leafdark" },
  { icon: Flame, value: "25+", label: "godina sa vama", cls: "text-ember" },
  { icon: Bike, value: "2", label: "platforme dostave", cls: "text-leafdark" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="pocetna" ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-[76px] bg-white">
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(9)].map((_, i) => (
          <span
            key={i}
            className="ember-particle"
            style={{
              left: `${8 + i * 11}%`,
              width: `${5 + (i % 3) * 4}px`,
              height: `${5 + (i % 3) * 4}px`,
              animationDuration: `${6 + i * 1.4}s`,
              animationDelay: `${i * 0.9}s`,
            }}
          />
        ))}
      </div>

      <div className="absolute -top-40 right-[-15%] w-[60vw] h-[60vw] rounded-full bg-ember/8 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-52 left-[-12%] w-[45vw] h-[45vw] rounded-full bg-leaf/10 blur-[130px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-[1.35fr_1fr] gap-12 items-center relative z-10">
        <motion.div style={{ opacity: fade }}>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="font-mono-x text-xs uppercase tracking-[0.35em] text-leafdark mb-6"
            data-testid="hero-eyebrow"
          >
            Roštilj · Kragujevac · Since 2000
          </motion.p>

          <h1 data-testid="hero-title" className="font-display font-black uppercase leading-[0.92] tracking-tight">
            <MaskedLine delay={0.15} className="text-[13vw] lg:text-[6.5rem] xl:text-[7.5rem] text-leaf">
              Najjači
            </MaskedLine>
            <MaskedLine delay={0.3} className="text-[13vw] lg:text-[6.5rem] xl:text-[7.5rem] text-ember">
              Roštilj
            </MaskedLine>
            <MaskedLine delay={0.45} className="text-[13vw] lg:text-[6.5rem] xl:text-[7.5rem] text-stroke-chili">
              U gradu
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="text-leafdark text-base md:text-lg leading-relaxed max-w-xl mt-8"
          >
            Sveže meso, ćumur, lepinja sa žara i 20 priloga po izboru. Radimo do duboko u
            noć, a dostava stiže preko Glovo i Wolt aplikacija — vrela i brza.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-center gap-4 mt-10"
          >
            <a
              data-testid="hero-order-glovo-button"
              href={BUSINESS.glovo}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 bg-ember text-white font-bold text-sm uppercase tracking-wide rounded-full px-8 py-4 hover:bg-emberglow transition-colors duration-300 shadow-[0_18px_35px_-12px_rgba(245,98,15,0.55)]"
            >
              <Bike className="w-4 h-4 transition-transform duration-300 group-hover:scale-125" />
              Naruči preko Glovo-a
            </a>
            <a
              data-testid="hero-order-wolt-button"
              href={BUSINESS.wolt}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 border-2 border-leaf text-leafdark font-bold text-sm uppercase tracking-wide rounded-full px-8 py-[14px] hover:bg-leaf hover:text-white transition-colors duration-300"
            >
              Naruči preko Wolt-a
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="flex flex-wrap gap-x-6 gap-y-2 mt-5"
          >
            {BUSINESS.phones.map((p) => (
              <a
                key={p.tel}
                data-testid={`hero-phone-${p.tel}`}
                href={`tel:${p.tel}`}
                className="flex items-center gap-2 font-mono-x text-xs tracking-[0.1em] text-leafdark hover:text-ember transition-colors"
              >
                <Phone className="w-3.5 h-3.5 text-ember" /> {p.label}
              </a>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 1 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-12 pt-8 border-t border-leaf/20"
          >
            {STATS.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label.replace(/\s+/g, "-")}`}>
                <s.icon className={`w-4 h-4 mb-2 ${s.cls}`} />
                <p className={`font-display font-bold text-xl ${s.cls}`}>{s.value}</p>
                <p className="font-mono-x text-[10px] uppercase tracking-[0.15em] text-leafdark/70 mt-1">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden lg:block"
        >
          <div className="relative overflow-hidden rounded-[2rem] border-2 border-ember/15 aspect-[4/5] shadow-[0_40px_80px_-30px_rgba(23,122,60,0.3)]">
            <motion.img
              src="/images/rostilj.jpg"
              alt="Sveže meso spremno za roštilj"
              data-testid="hero-image"
              style={{ y: imgY }}
              className="w-full h-[120%] object-cover scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-leafdark/25 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white rounded-full shadow-[0_16px_40px_-14px_rgba(27,21,18,0.3)]">
            <svg viewBox="0 0 100 100" className="animate-spin-slow w-full h-full">
              <defs>
                <path id="circlePath" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
              </defs>
              <text className="fill-leafdark font-mono-x" style={{ fontSize: "9.5px", letterSpacing: "2.5px" }}>
                <textPath href="#circlePath">DOSTAVA · GLOVO · WOLT · KRAGUJEVAC ·</textPath>
              </text>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <Flame className="w-6 h-6 text-chili" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
